<script setup lang="ts">
import { userLeftTheField } from '@/utils/field-leave'
import { isEmptyValue, type FieldState } from '@/utils/field-state'
import { computed, nextTick, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PositionAddRow from './PositionAddRow.vue'
import PositionCell from './PositionCell.vue'
import { formatCell, isNumericColumn, matchingSuggestion } from './positions'
import type { PositionCellValue, PositionColumn, PositionRow, PositionTotalRow } from './types'

const props = withDefaults(
  defineProps<{
    columns: PositionColumn[]
    editableRows: boolean
    showTotal: boolean
    maxFractionDigits: number
    addLabel?: string
    emptyLabel?: string
    inset?: boolean
    totalRows?: PositionTotalRow[]
  }>(),
  {
    addLabel: undefined,
    emptyLabel: undefined,
    inset: false,
    totalRows: undefined,
  },
)

const rows = defineModel<PositionRow[]>({ default: () => [] })
const totalInputs = defineModel<Record<string, PositionCellValue>>('totalInputs', { default: () => ({}) })

const emit = defineEmits<{
  addRow: []
  removeRow: [key: string]
  commit: []
  commitTotal: [key: string]
}>()

const { t, locale } = useI18n({ useScope: 'local' })

const cellLabels = computed(() => ({ empty: t('emptyCell'), yes: t('boolean.yes'), no: t('boolean.no') }))

/** A table that reaches the card edge keeps its rules full width but holds its content off the edge. */
const insetClass = computed(() => (props.inset ? 'first:pl-6 last:pr-6' : ''))

/**
 * A value sits behind its field's border and padding, so heading and total need the same offset to
 * stand on the same edge as the values between them.
 */
const alignedPadding = (column: PositionColumn): string => {
  if (column.type === 'boolean') {
    return 'px-2'
  }

  return isNumericColumn(column) ? 'pl-2 pr-[18px]' : 'pl-[18px] pr-2'
}

const alignedInsetClass = computed(() => (props.inset ? 'first:pl-[32px] last:pr-6' : ''))

const columnCount = computed(() => props.columns.length + (props.editableRows ? 1 : 0))

/** An editable list already invites the first row through its add button, so it stays silent without an own label. */
const emptyText = computed(() => props.emptyLabel ?? (props.editableRows ? null : t('empty')))

const lastEditableColumnKey = computed(
  () => [...props.columns].reverse().find((column) => !column.readonly)?.key ?? null,
)

/** The name column stays untouched: a row's state describes the figures, not what it is called. */
function carriesState(row: PositionRow, column: PositionColumn): boolean {
  return Boolean(row.state) && !column.readonly && column.type !== 'suggest'
}

/** Only what is still empty waits for a value, so a filled cell drops back to normal. */
function cellState(row: PositionRow, column: PositionColumn): FieldState | null {
  const value = row.cells[column.key]

  return carriesState(row, column) && isEmptyValue(value) ? (row.state ?? null) : null
}

function cellColumn(row: PositionRow, column: PositionColumn): PositionColumn {
  const stated = cellState(row, column) === null ? column : { ...column, placeholder: row.statePlaceholder }

  if (!row.columnType || column.readonly || column.type === 'suggest') {
    return stated
  }

  return { ...stated, type: row.columnType, total: false }
}

function cellTitle(row: PositionRow, column: PositionColumn): string | undefined {
  return cellState(row, column) === null ? undefined : row.stateHint
}

/** Editing turns a preview row into a real one; its remaining empty cells keep waiting. */
function updatedRow(row: PositionRow, column: PositionColumn, value: PositionCellValue): PositionRow {
  const cells = { ...row.cells, [column.key]: value }

  if (column.type !== 'suggest') {
    return { ...row, cells, placeholder: false }
  }

  return { ...row, cells, placeholder: false, predefinedKey: matchingSuggestion(column, value)?.key ?? null }
}

function setCell(rowKey: string, column: PositionColumn, value: PositionCellValue): void {
  rows.value = rows.value.map((row) => (row.key === rowKey ? updatedRow(row, column, value) : row))
}

const showTotals = computed(() => props.showTotal && props.columns.some((column) => column.total))

const shownTotalRows = computed<PositionTotalRow[]>(() => props.totalRows ?? [{ key: 'total', label: t('total') }])

function columnTotal(column: PositionColumn, totalRow: PositionTotalRow): number | null {
  const values = rows.value
    .filter((row) => !row.columnType && (totalRow.includes?.(row) ?? true))
    .map((row) => row.cells[column.key])
    .filter((value): value is number => typeof value === 'number')

  return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0)
}

function displayTotal(column: PositionColumn, totalRow: PositionTotalRow): string {
  return formatCell(column, columnTotal(column, totalRow), {
    locale: locale.value,
    labels: cellLabels.value,
    maxFractionDigits: props.maxFractionDigits,
  })
}

const isTotalInput = (totalRow: PositionTotalRow, column: PositionColumn): boolean =>
  totalRow.inputColumn === column.key

const totalRowBorderClass = (totalIndex: number): string =>
  totalIndex === 0 ? 'border-line border-t-2 pt-3' : 'pt-1.5'

function setTotalInput(key: string, value: PositionCellValue): void {
  totalInputs.value = { ...totalInputs.value, [key]: value }
}

function commitTotalOnLeave(key: string): void {
  if (userLeftTheField()) {
    emit('commitTotal', key)
  }
}

const table = useTemplateRef<HTMLTableElement>('table')

let awaitingNewRow = false

watch(
  () => rows.value.length,
  (length, previous) => {
    if (!awaitingNewRow || length <= previous) {
      return
    }

    awaitingNewRow = false

    void nextTick().then(() => {
      const rendered = table.value?.querySelectorAll<HTMLElement>('tbody tr[data-position-row]')

      rendered?.[rendered.length - 1]?.querySelector<HTMLElement>('input:not([disabled]), select')?.focus()
    })
  },
)

function addRow(): void {
  awaitingNewRow = true
  emit('addRow')
}

function onLastCellTab(rowIndex: number, column: PositionColumn, event: KeyboardEvent): void {
  const isLastCell = rowIndex === rows.value.length - 1 && column.key === lastEditableColumnKey.value

  if (event.shiftKey || !props.editableRows || !isLastCell) {
    return
  }

  event.preventDefault()
  addRow()
}
</script>

<template>
  <table ref="table" class="w-full border-collapse">
    <thead>
      <tr>
        <th
          v-for="column in columns"
          :key="column.key"
          class="border-line section-label border-b pb-3"
          :class="[
            alignedPadding(column),
            isNumericColumn(column) ? 'text-right' : 'text-left',
            column.width,
            alignedInsetClass,
          ]">
          {{ column.label }}
        </th>
        <th v-if="editableRows" class="border-line w-9 border-b px-2 pb-3" :class="alignedInsetClass"></th>
      </tr>
    </thead>

    <tbody>
      <tr v-if="!rows.length && emptyText">
        <td class="text-ink-3 p-3.5 text-center align-middle" :class="insetClass" :colspan="columnCount">
          {{ emptyText }}
        </td>
      </tr>

      <tr v-for="(row, rowIndex) in rows" :key="row.key" data-position-row>
        <td
          v-for="column in columns"
          :key="column.key"
          class="px-2 py-[9px] align-middle"
          :class="[isNumericColumn(column) ? 'text-right' : 'text-left', insetClass]"
          :title="cellTitle(row, column)"
          @keydown.tab="onLastCellTab(rowIndex, column, $event)">
          <PositionCell
            :column="cellColumn(row, column)"
            :value="row.cells[column.key] ?? null"
            :readonly="Boolean(column.readonly)"
            :labels="cellLabels"
            :max-fraction-digits="maxFractionDigits"
            :state="cellState(row, column)"
            @update="(value) => setCell(row.key, column, value)"
            @commit="emit('commit')" />
        </td>

        <td v-if="editableRows" class="w-9 px-2 py-[9px] align-middle" :class="insetClass">
          <button
            v-if="!row.placeholder"
            type="button"
            class="text-ink-3 hover:text-danger hover:bg-danger-subtle cursor-pointer rounded-md border-none bg-transparent p-2 leading-none"
            :title="t('remove')"
            :aria-label="t('remove')"
            @click="emit('removeRow', row.key)">
            <i class="pi pi-trash text-[13px]" aria-hidden="true" />
          </button>
        </td>
      </tr>

      <PositionAddRow
        v-if="editableRows"
        :colspan="columns.length"
        :show-actions="editableRows"
        :label="addLabel"
        :inset="inset"
        @add="addRow" />
    </tbody>

    <tfoot v-if="showTotals">
      <tr v-for="(totalRow, totalIndex) in shownTotalRows" :key="totalRow.key">
        <template v-for="(column, index) in columns" :key="column.key">
          <td
            v-if="isTotalInput(totalRow, column)"
            class="px-2 pb-1.5 align-middle"
            :class="[totalRowBorderClass(totalIndex), insetClass]"
            @focusout="commitTotalOnLeave(totalRow.key)"
            @keydown.enter.prevent="emit('commitTotal', totalRow.key)">
            <PositionCell
              :column="{ ...column, label: totalRow.label }"
              :value="totalInputs[totalRow.key] ?? null"
              :readonly="false"
              :labels="cellLabels"
              :max-fraction-digits="maxFractionDigits"
              :state="totalRow.state ?? null"
              @update="(value) => setTotalInput(totalRow.key, value)" />
          </td>
          <td
            v-else
            class="text-ink pb-1.5 align-middle font-bold tabular-nums"
            :class="[
              totalRowBorderClass(totalIndex),
              alignedPadding(column),
              isNumericColumn(column) ? 'text-right' : 'text-left',
              alignedInsetClass,
            ]">
            <span v-if="column.total">{{ displayTotal(column, totalRow) }}</span>
            <span v-else-if="index === 0">{{ totalRow.label }}</span>
          </td>
        </template>
        <td
          v-if="editableRows"
          class="w-9 px-2 pb-1.5"
          :class="[totalRowBorderClass(totalIndex), alignedInsetClass]"></td>
      </tr>
    </tfoot>
  </table>
</template>

<i18n lang="json">
{
  "de": {
    "remove": "Position entfernen",
    "empty": "Keine Positionen – Zeile hinzufügen.",
    "emptyCell": "—",
    "total": "Summe",
    "boolean": {
      "yes": "Ja",
      "no": "Nein"
    }
  },
  "en": {
    "remove": "Remove position",
    "empty": "No positions – add a row.",
    "emptyCell": "—",
    "total": "Total",
    "boolean": {
      "yes": "Yes",
      "no": "No"
    }
  }
}
</i18n>
